'use client'

import React, { ReactNode } from 'react'
import {
    Breadcrumb,
    BreadcrumbItem,
    BreadcrumbLink,
    BreadcrumbList,
    BreadcrumbPage,
    BreadcrumbSeparator,
} from '@/components/ui/breadcrumb'
import { cn } from '@/lib/utils'
import { usePathname } from 'next/navigation'
import { SidebarToggle } from '@/components/sidebar/sidebar-toggle'
import { useSidebar } from '@/components/sidebar/sidebar-provider'
import { AdminNotificationBar } from '@/components/auth/dev-features/notification-bar'
import { AccessGate } from './auth/access-gate'
import { Button } from '@/components/ui/button'
import { User } from 'lucide-react'
import Link from 'next/link'
import { useUserContext } from '@/components/providers'
import { BlendAINewLogo } from '@/components/ui/icons'
import { motion } from 'motion/react'
import { PromptImprover } from '@/components/auth/dev-features/prompt-improver'


// Main container component
interface InsetProps {
    children: React.ReactNode
    className?: string
}

export const Inset = ({ children, className }: InsetProps) => {
    const { open } = useSidebar()

    return (
        <div
            className={cn(
                "w-full h-full flex flex-col md:p-3 md:pt-0 bg-background4",
                !open && "md:pl-3",
                className
            )}
        >
            {children}
        </div>
    )
}

// Header component with breadcrumbs
interface InsetHeaderProps {
    breadcrumbs?: string[]
    title?: string
    description?: string
    children?: ReactNode
    className?: string
}

export const InsetHeader = ({ breadcrumbs = [], title, description, children, className }: InsetHeaderProps) => {
    const pathname = usePathname()
    const { isUserAuthenticated } = useUserContext()

    // Display error if both breadcrumbs and title/description are provided
    if (breadcrumbs.length > 0 && (title || description)) {
        console.error('InsetHeader: Cannot use both breadcrumbs and title/description props at the same time')
        return null
    }

    // Auto-generate breadcrumbs from pathname if none provided and no title/description
    const finalBreadcrumbs = breadcrumbs.length === 0 && !title && !description
        ? pathname.split('/').filter(Boolean).map(segment =>
            segment.charAt(0).toUpperCase() + segment.slice(1)
        )
        : breadcrumbs

    return (
        <>
            {/* Mobile layout (ChatGPT-like) */}
            <header className={cn(
                "flex md:hidden w-full h-12 shrink-0 items-center justify-between px-4 transition-[width,height] ease-linear group-has-[[data-collapsible=icon]]/sidebar-wrapper:h-12",
                className
            )}>
                <AccessGate developerFeature="adminNotificationBar">
                    <AdminNotificationBar />
                </AccessGate>
                
                {/* Left side - Children */}
                <div className="flex items-center gap-2">
                    {children}
                </div>

                {/* Center - App name */}
                <div className="absolute left-1/2 transform -translate-x-1/2">
                    <div className="flex flex-row gap-2 items-center hover:cursor-pointer" onClick={() => window.location.href = "/"}>
                        <BlendAINewLogo
                            className="h-8 w-8 rounded-full hover:animate-spin"
                            fillColor="transparent"
                            strokeAndCircleColor="#EA580C"
                        />
                        <h1 className="font-medium text-lg">Blend</h1>
                        <div className="font-normal text-xs text-muted-foreground -translate-x-1.5 translate-y-1">beta</div>
                    </div>
                </div>

                {/* Right side - Buttons */}
                <div className="flex items-center gap-2">
                    <AccessGate
                        authFallback={
                            <Button
                                asChild
                                variant="ghost"
                                size="sm"
                                className="h-8 w-8 p-0"
                            >
                                <Link href="/auth/log-in">
                                    <User className="h-4 w-4" />
                                    <span className="sr-only">Sign in</span>
                                </Link>
                            </Button>
                        }
                    >
                        <SidebarToggle />
                    </AccessGate>
                </div>
            </header>

            {/* Desktop layout (existing behavior) */}
            <header className={cn(
                "hidden md:flex w-full h-12 shrink-0 items-center gap-2 transition-[width,height] ease-linear group-has-[[data-collapsible=icon]]/sidebar-wrapper:h-12",
                className
            )}>
                <AccessGate developerFeature="adminNotificationBar">
                    <AdminNotificationBar />
                </AccessGate>
                <div className="flex items-center gap-2 flex-1 mr-3 md:mr-0">
                    {/* Render title and description if provided */}
                    {(title || description) && (
                        <div className="flex items-center gap-2 text-sm">
                            {title && (
                                <h1 className="font-medium">{title}</h1>
                            )}
                            {title && description && (
                                <div className="h-4 w-px mx-2 bg-border" />
                            )}
                            {description && (
                                <span className="text-muted-foreground">{description}</span>
                            )}
                        </div>
                    )}

                    {/* Render breadcrumbs if no title/description */}
                    {finalBreadcrumbs.length > 0 && !title && !description && (
                        <div className="">
                            <Breadcrumb>
                                <BreadcrumbList>
                                    {finalBreadcrumbs.map((crumb, index) => (
                                        <div key={index} className="flex items-center">
                                            <BreadcrumbItem className={index === 0 ? "hidden md:block" : ""}>
                                                {index === finalBreadcrumbs.length - 1 ? (
                                                    <BreadcrumbPage className={finalBreadcrumbs.length === 1 ? "font-medium" : ""}>{crumb}</BreadcrumbPage>
                                                ) : (
                                                    <BreadcrumbLink href="#">{crumb}</BreadcrumbLink>
                                                )}
                                            </BreadcrumbItem>
                                            {index < finalBreadcrumbs.length - 1 && (
                                                <BreadcrumbSeparator className="hidden md:block" />
                                            )}
                                        </div>
                                    ))}
                                </BreadcrumbList>
                            </Breadcrumb>
                        </div>
                    )}
                    {children && (
                        <div className="ml-auto flex items-center gap-2">
                            {children}
                        </div>
                    )}
                    <PromptImprover />
                </div>
            </header>
        </>
    )
}

// Content title component positioned next to sidebar toggle
interface InsetDescriptionProps {
    title: string
    description?: string
    className?: string
}

export const InsetDescription = ({ title, description, className }: InsetDescriptionProps) => {
    return (
        <div className={cn(
            "flex flex-col gap-1",
            className
        )}>
            <h1 className="text-2xl font-semibold tracking-tight">{title}</h1>
            {description && (
                <p className="text-muted-foreground text-sm">{description}</p>
            )}
        </div>
    )
}

// Content component
interface InsetContentProps {
    children: React.ReactNode
    className?: string
    withPadding?: boolean
    scrollable?: boolean
    hideSidebarToggle?: boolean
}

export const InsetContent = ({
    children,
    className,
    withPadding = true,
    scrollable = true,
    hideSidebarToggle = false,
}: InsetContentProps) => {
    const { open } = useSidebar()
    const isOpen = open

    return (
        <motion.main
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ 
                duration: 0.3,
                ease: [0.25, 0.46, 0.45, 0.94] // ease-out-quad
            }}
            className={cn(
                "flex-1 flex flex-col items-center min-h-0 p-2 border-t md:border border-border bg-background6 md:rounded-b-lg rounded-t-lg relative shadow-md overflow-hidden",
                withPadding && "p-3 md:p-3",
                className
            )}
        >
            {!hideSidebarToggle && (
                <div className="absolute top-3 left-3  hidden md:block">
                    <SidebarToggle />
                </div>
            )}
            {children}
        </motion.main>
    )
}

